import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { paymentGatewayApi, paymentGatewayQueryKeys } from "../../shared/api/paymentGateway";
import { apiError } from "../../shared/api/client";
import { formatVndWithUnit } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Cổng thanh toán có giấy phép (#18998, tầng 3 của ADR-0050).
 *
 * <p>Phòng khám KHÔNG bắt buộc dùng. Mã QR ngân hàng đã thu tiền được, miễn phí, mọi ngân hàng.
 * Tầng này chỉ thêm đúng hai thứ: <b>hoàn tiền tự động</b> và <b>thẻ/ví</b> — và đổi lại là phí
 * theo phần trăm, cùng thủ tục KYC merchant.
 */
export function PaymentGatewaySettingsPage() {
  const qc = useQueryClient();
  const options = useQuery({
    queryKey: paymentGatewayQueryKeys.options(),
    queryFn: paymentGatewayApi.options,
    staleTime: 60 * 60 * 1000,
  });
  const configs = useQuery({
    queryKey: paymentGatewayQueryKeys.list(),
    queryFn: paymentGatewayApi.list,
  });
  const usage = useQuery({
    queryKey: paymentGatewayQueryKeys.usage(),
    queryFn: paymentGatewayApi.usage,
  });

  const active = configs.data?.find((c) => c.active) ?? null;
  const [provider, setProvider] = useState("");
  const [merchantCode, setMerchantCode] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const id = await paymentGatewayApi.create({
        provider,
        merchantCode: merchantCode.trim(),
        secret,
      });
      await paymentGatewayApi.activate(id);
    },
    onSuccess: () => {
      setError(null);
      setSecret("");
      setMsg("Đã lưu và kích hoạt.");
      qc.invalidateQueries({ queryKey: paymentGatewayQueryKeys.list() });
    },
    onError: (e) => {
      setMsg(null);
      setError(apiError(e).detail);
    },
  });

  const chosen = options.data?.find((o) => o.code === provider);
  const valid = Boolean(provider) && merchantCode.trim() !== "" && secret !== "";

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <PageHeader
        title="Cổng thanh toán"
        description="Không bắt buộc. Mã QR ngân hàng đã thu tiền được và miễn phí — cổng chỉ thêm hoàn tiền tự động và thanh toán bằng thẻ / ví."
      />

      {usage.data && <UsageCard usage={usage.data} />}

      {active && (
        <Card>
          <CardContent className="p-5 text-sm">
            Đang dùng: <b>{active.providerName}</b> · mã merchant{" "}
            <span className="font-mono">{active.merchantCode}</span>
            {active.hasSecret ? " · đã có khoá bí mật" : " · CHƯA có khoá bí mật"}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="space-y-1.5">
            <Label htmlFor="gw">Cổng</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger id="gw">
                <SelectValue placeholder="Chọn cổng thanh toán" />
              </SelectTrigger>
              <SelectContent>
                {(options.data ?? []).map((o) => (
                  <SelectItem key={o.code} value={o.code}>
                    {o.name}
                    {o.refundApi ? "" : " — chưa rõ hoàn tiền"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosen && (
              <p className="text-xs text-muted-foreground">
                {chosen.note}
                {!chosen.refundApi && (
                  <>
                    {" "}
                    <b className="text-amber-700">
                      Cổng này chưa chắc hoàn tiền được qua API — mà hoàn tiền là lý do chính để
                      dùng cổng.
                    </b>
                  </>
                )}
              </p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="mc">Mã merchant</Label>
              <Input
                id="mc"
                value={merchantCode}
                onChange={(e) => setMerchantCode(e.target.value)}
                placeholder="Do cổng cấp khi duyệt hồ sơ"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sk">Khoá bí mật</Label>
              <Input
                id="sk"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Khoá bí mật được mã hoá khi lưu và <b>không đọc lại được</b> — kể cả bởi chủ phòng
            khám. Đổi khoá là nhập đè khoá mới.
          </p>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          {msg && <p className="rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">{msg}</p>}

          <div className="flex items-center gap-3">
            <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
              {save.isPending ? "Đang lưu…" : "Lưu và kích hoạt"}
            </Button>
            {active && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  paymentGatewayApi.disableAll().then(() =>
                    qc.invalidateQueries({ queryKey: paymentGatewayQueryKeys.list() }),
                  )
                }
              >
                Ngừng dùng cổng
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Mức dùng so với trần. Phòng khám phải thấy con số này TRƯỚC khi nhận hoá đơn phần vượt — một
 * khoản phí chỉ xuất hiện lúc thanh toán là một khoản phí gây tranh cãi.
 */
function UsageCard({
  usage,
}: {
  usage: { capVnd: number | null; usedVnd: number; overageVnd: number };
}) {
  const pct =
    usage.capVnd && usage.capVnd > 0
      ? Math.min(100, Math.round((usage.usedVnd / usage.capVnd) * 100))
      : 0;
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <h2 className="text-sm font-semibold">Doanh thu qua cổng tháng này</h2>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <span>
            Đã dùng <b className="tabular-nums">{formatVndWithUnit(usage.usedVnd)}</b>
          </span>
          <span className="text-muted-foreground">
            Gói bao{" "}
            <b className="tabular-nums text-foreground">
              {usage.capVnd == null ? "không bao" : formatVndWithUnit(usage.capVnd)}
            </b>
          </span>
          {usage.overageVnd > 0 && (
            <span className="text-amber-700">
              Vượt <b className="tabular-nums">{formatVndWithUnit(usage.overageVnd)}</b>
            </span>
          )}
        </div>
        {usage.capVnd != null && usage.capVnd > 0 && (
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full ${usage.overageVnd > 0 ? "bg-amber-500" : "bg-primary"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Chỉ tính tiền thu qua cổng có phí. Tiền vào bằng <b>mã QR ngân hàng không tính</b> — nó
          không mất phí.
        </p>
      </CardContent>
    </Card>
  );
}
